import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
  Alert,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocalOffer as CouponIcon,
  ContentCopy as CopyIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { adminService } from '../../services/api';

const AdminCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [openUsageDialog, setOpenUsageDialog] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    type: 'percentage', // percentage, fixed, shipping
    value: '',
    applicationType: 'all', // all, specific-products, category, first-time-users
    specificProducts: [],
    category: '',
    usageLimit: '',
    usageType: 'unlimited', // unlimited, limited, per-user
    perUserLimit: 1,
    minOrderValue: '',
    maxDiscountAmount: '',
    startDate: '',
    endDate: '',
    isActive: true
  });

  useEffect(() => {
    loadCoupons();
    loadProducts();
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const res = await adminService.getCategories();
      setCategories(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Failed to load categories', e);
      setCategories([]);
    }
  };

  const loadCoupons = async () => {
    try {
      setLoading(true);
      const response = await adminService.getCoupons();
      setCoupons(response.data.coupons || []);
    } catch (error) {
      console.error('Error loading coupons:', error);
      toast.error('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      // You can integrate with actual product API here
      const mockProducts = [
        { id: '1', name: 'Test Product 1', category: 'Mugs' },
        { id: '2', name: 'Test Product 2', category: 'T-Shirts' }
      ];
      setProducts(mockProducts);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      description: '',
      type: 'percentage',
      value: '',
      applicationType: 'all',
      specificProducts: [],
      category: '',
      usageLimit: '',
      usageType: 'unlimited',
      perUserLimit: 1,
      minOrderValue: '',
      maxDiscountAmount: '',
      startDate: '',
      endDate: '',
      isActive: true
    });
    setEditingCoupon(null);
  };

  const handleOpenDialog = (coupon = null) => {
    if (coupon) {
      setEditingCoupon(coupon);
      setFormData({
        code: coupon.code,
        description: coupon.description,
        type: coupon.type,
        value: coupon.value,
        applicationType: coupon.applicationType,
        specificProducts: coupon.specificProducts || [],
        category: coupon.category || '',
        usageLimit: coupon.usageLimit || '',
        usageType: coupon.usageType,
        perUserLimit: coupon.perUserLimit || 1,
        minOrderValue: coupon.minOrderValue || '',
        maxDiscountAmount: coupon.maxDiscountAmount || '',
        startDate: coupon.startDate,
        endDate: coupon.endDate,
        isActive: coupon.isActive
      });
    } else {
      resetForm();
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetForm();
  };

  const handleFormChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!formData.code.trim()) {
      toast.error('Coupon code is required');
      return false;
    }
    if (!formData.description.trim()) {
      toast.error('Description is required');
      return false;
    }
    // Only require value for non-shipping coupons
    if (formData.type !== 'shipping' && (!formData.value || formData.value <= 0)) {
      toast.error('Valid discount value is required');
      return false;
    }
    if (formData.type === 'percentage' && formData.value > 100) {
      toast.error('Percentage discount cannot exceed 100%');
      return false;
    }
    if (formData.usageType === 'limited' && (!formData.usageLimit || formData.usageLimit <= 0)) {
      toast.error('Usage limit is required for limited coupons');
      return false;
    }
    return true;
  };

  const handleSaveCoupon = async () => {
    if (!validateForm()) return;

    try {
      const couponData = {
        ...formData,
        code: formData.code.toUpperCase(),
        value: parseFloat(formData.value),
        usageLimit: formData.usageType === 'limited' ? parseInt(formData.usageLimit) : null,
        perUserLimit: formData.usageType === 'per-user' ? parseInt(formData.perUserLimit) : null,
        minOrderValue: formData.minOrderValue ? parseFloat(formData.minOrderValue) : 0,
        maxDiscountAmount: formData.maxDiscountAmount ? parseFloat(formData.maxDiscountAmount) : null
      };

      if (editingCoupon) {
        // Update existing coupon
        await adminService.updateCoupon(editingCoupon.id, couponData);
        toast.success('Coupon updated successfully');
      } else {
        // Create new coupon
        await adminService.createCoupon(couponData);
        toast.success('Coupon created successfully');
      }

      handleCloseDialog();
      loadCoupons(); // Reload coupons list
    } catch (error) {
      console.error('Error saving coupon:', error);
      const errorMessage = error.response?.data?.error || 'Failed to save coupon';
      toast.error(errorMessage);
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;

    try {
      await adminService.deleteCoupon(couponId);
      toast.success('Coupon deleted successfully');
      loadCoupons(); // Reload coupons list
    } catch (error) {
      console.error('Error deleting coupon:', error);
      const errorMessage = error.response?.data?.error || 'Failed to delete coupon';
      toast.error(errorMessage);
    }
  };

  const handleToggleStatus = async (couponId, isActive) => {
    try {
      await adminService.toggleCouponStatus(couponId, { isActive: !isActive });
      toast.success(`Coupon ${!isActive ? 'activated' : 'deactivated'} successfully`);
      loadCoupons(); // Reload coupons list
    } catch (error) {
      console.error('Error toggling coupon status:', error);
      const errorMessage = error.response?.data?.error || 'Failed to update coupon status';
      toast.error(errorMessage);
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Coupon code copied to clipboard');
  };

  const handleViewUsage = (coupon) => {
    setSelectedCoupon(coupon);
    setOpenUsageDialog(true);
  };

  const getUsageProgress = (coupon) => {
    if (coupon.usageType === 'unlimited') return null;
    return (coupon.usageCount / coupon.usageLimit) * 100;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <LinearProgress sx={{ width: '100%', maxWidth: 300 }} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" component="h1">
          <CouponIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
          Coupon Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          size="large"
        >
          Create Coupon
        </Button>
      </Box>

      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">Total Coupons</Typography>
              <Typography variant="h3">{coupons.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success.main">Active Coupons</Typography>
              <Typography variant="h3">{coupons.filter(c => c.isActive).length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="warning.main">Total Usage</Typography>
              <Typography variant="h3">{coupons.reduce((sum, c) => sum + c.usageCount, 0)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="error.main">Expired</Typography>
              <Typography variant="h3">
                {coupons.filter(c => c.endDate && new Date(c.endDate) < new Date()).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Code</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell><strong>Value</strong></TableCell>
                  <TableCell><strong>Usage</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Valid Until</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {coupons.map((coupon) => (
                  <TableRow key={coupon.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Typography variant="body2" fontWeight="bold">
                          {coupon.code}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleCopyCode(coupon.code)}
                          sx={{ ml: 1 }}
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{coupon.description}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={coupon.type.toUpperCase()} 
                        size="small" 
                        color={coupon.type === 'percentage' ? 'primary' : 
                               coupon.type === 'fixed' ? 'secondary' : 'info'}
                      />
                    </TableCell>
                    <TableCell>
                      {coupon.type === 'percentage' ? `${coupon.value}%` : 
                       coupon.type === 'fixed' ? `₹${coupon.value}` : 'Free Shipping'}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {coupon.usageCount} {coupon.usageType !== 'unlimited' && `/ ${coupon.usageLimit}`}
                        </Typography>
                        {coupon.usageType === 'per-user' && coupon.userUsage && (
                          <Typography variant="caption" color="text.secondary">
                            {Object.keys(coupon.userUsage).length} users
                          </Typography>
                        )}
                        {getUsageProgress(coupon) && (
                          <LinearProgress 
                            variant="determinate" 
                            value={getUsageProgress(coupon)} 
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={coupon.isActive}
                            onChange={() => handleToggleStatus(coupon.id, coupon.isActive)}
                            size="small"
                          />
                        }
                        label={coupon.isActive ? 'Active' : 'Inactive'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {coupon.endDate ? new Date(coupon.endDate).toLocaleDateString() : 'No expiry'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {coupon.usageType === 'per-user' && coupon.userUsage && Object.keys(coupon.userUsage).length > 0 && (
                        <IconButton
                          size="small"
                          onClick={() => handleViewUsage(coupon)}
                          color="info"
                          title="View User Usage"
                        >
                          <ViewIcon />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(coupon)}
                        color="primary"
                        title="Edit Coupon"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteCoupon(coupon.id)}
                        color="error"
                        title="Delete Coupon"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create/Edit Coupon Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Coupon Code*"
                value={formData.code}
                onChange={handleFormChange('code')}
                placeholder="SAVE10"
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Discount Type*</InputLabel>
                <Select
                  value={formData.type}
                  onChange={handleFormChange('type')}
                  label="Discount Type*"
                >
                  <MenuItem value="percentage">Percentage Off</MenuItem>
                  <MenuItem value="fixed">Fixed Amount Off</MenuItem>
                  <MenuItem value="shipping">Free Shipping</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description*"
                value={formData.description}
                onChange={handleFormChange('description')}
                placeholder="Brief description of the offer"
              />
            </Grid>

            {formData.type !== 'shipping' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={formData.type === 'percentage' ? 'Percentage (%)' : 'Amount (₹)'}
                  type="number"
                  value={formData.value}
                  onChange={handleFormChange('value')}
                  inputProps={{ min: 0, max: formData.type === 'percentage' ? 100 : undefined }}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Apply To</InputLabel>
                <Select
                  value={formData.applicationType}
                  onChange={handleFormChange('applicationType')}
                  label="Apply To"
                >
                  <MenuItem value="all">All Products</MenuItem>
                  <MenuItem value="specific-products">Specific Products</MenuItem>
                  <MenuItem value="category">Product Category</MenuItem>
                  <MenuItem value="first-time-users">First Time Users Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {formData.applicationType === 'category' && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={formData.category}
                    onChange={handleFormChange('category')}
                    label="Category"
                  >
                    {categories.length === 0 && (
                      <MenuItem value="" disabled>Loading...</MenuItem>
                    )}
                    {categories.map((cat) => (
                      <MenuItem key={cat.id || cat._id || cat.name} value={cat.id || cat._id || cat.name}>{cat.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Usage Type</InputLabel>
                <Select
                  value={formData.usageType}
                  onChange={handleFormChange('usageType')}
                  label="Usage Type"
                >
                  <MenuItem value="unlimited">Unlimited Usage</MenuItem>
                  <MenuItem value="limited">Limited Total Usage</MenuItem>
                  <MenuItem value="per-user">Limited Per User</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {formData.usageType === 'limited' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Usage Limit"
                  type="number"
                  value={formData.usageLimit}
                  onChange={handleFormChange('usageLimit')}
                  inputProps={{ min: 1 }}
                />
              </Grid>
            )}

            {formData.usageType === 'per-user' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Uses Per User"
                  type="number"
                  value={formData.perUserLimit}
                  onChange={handleFormChange('perUserLimit')}
                  inputProps={{ min: 1 }}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Minimum Order Value (₹)"
                type="number"
                value={formData.minOrderValue}
                onChange={handleFormChange('minOrderValue')}
                inputProps={{ min: 0 }}
              />
            </Grid>

            {formData.type === 'percentage' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Maximum Discount Amount (₹)"
                  type="number"
                  value={formData.maxDiscountAmount}
                  onChange={handleFormChange('maxDiscountAmount')}
                  inputProps={{ min: 0 }}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={formData.startDate}
                onChange={handleFormChange('startDate')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={formData.endDate}
                onChange={handleFormChange('endDate')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={handleFormChange('isActive')}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveCoupon} variant="contained">
            {editingCoupon ? 'Update' : 'Create'} Coupon
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Usage Dialog */}
      <Dialog 
        open={openUsageDialog} 
        onClose={() => setOpenUsageDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          User Usage History - {selectedCoupon?.code}
        </DialogTitle>
        <DialogContent>
          {selectedCoupon?.userUsage ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>User ID</strong></TableCell>
                    <TableCell><strong>Usage Count</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(selectedCoupon.userUsage).map(([userId, count]) => (
                    <TableRow key={userId}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {userId}
                        </Typography>
                      </TableCell>
                      <TableCell>{count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography>No usage data available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUsageDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminCoupons;
